import clsx from 'clsx';
import {
  useRef,
  useState,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';

import { useIsMobile } from '~/hooks/useIsMobile';

class DOMVector {
  constructor(
    readonly x: number,
    readonly y: number,
    readonly magnitudeX: number,
    readonly magnitudeY: number
  ) {
    this.x = x;
    this.y = y;
    this.magnitudeX = magnitudeX;
    this.magnitudeY = magnitudeY;
  }

  getDiagonalLength(): number {
    return Math.sqrt(Math.pow(this.magnitudeX, 2) + Math.pow(this.magnitudeY, 2));
  }

  toDOMRect(): DOMRect {
    return new DOMRect(
      Math.min(this.x, this.x + this.magnitudeX),
      Math.min(this.y, this.y + this.magnitudeY),
      Math.abs(this.magnitudeX),
      Math.abs(this.magnitudeY)
    );
  }

  toTerminalPoint(): DOMPoint {
    return new DOMPoint(this.x + this.magnitudeX, this.y + this.magnitudeY);
  }

  add(vector: DOMVector): DOMVector {
    return new DOMVector(
      this.x + vector.x,
      this.y + vector.y,
      this.magnitudeX + vector.magnitudeX,
      this.magnitudeY + vector.magnitudeY
    );
  }

  clamp(vector: DOMRect): DOMVector {
    return new DOMVector(
      this.x,
      this.y,
      Math.min(vector.width - this.x, this.magnitudeX),
      Math.min(vector.height - this.y, this.magnitudeY)
    );
  }
}

function intersect(rect1: DOMRect, rect2: DOMRect): boolean {
  if (rect1.right < rect2.left || rect2.right < rect1.left) return false;

  if (rect1.bottom < rect2.top || rect2.bottom < rect1.top) return false;

  return true;
}

function shallowEqual(x: Record<string, boolean>, y: Record<string, boolean>) {
  return Object.keys(x).length === Object.keys(y).length && Object.keys(x).every((key) => x[key] === y[key]);
}

interface SelectionStateValue {
  selectedItems: Record<string, boolean>;
  setSelectedItems: Dispatch<SetStateAction<Record<string, boolean>>>;
  /** Empties the selection and runs the provider's `onClear` (the library ends its select-all there). */
  clearSelection: () => void;
}

const SelectionStateContext = createContext<SelectionStateValue | null>(null);

export function SelectionStateProvider({ children, onClear }: { children: ReactNode; onClear?: () => void }) {
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const clearSelection = useCallback(() => {
    setSelectedItems({});
    onClear?.();
  }, [onClear]);

  return (
    <SelectionStateContext.Provider value={{ selectedItems, setSelectedItems, clearSelection }}>
      {children}
    </SelectionStateContext.Provider>
  );
}

interface DragSelectionValue {
  isDragging: boolean;
  setIsDragging: (value: boolean) => void;
  dragVector: DOMVector | null;
  setDragVector: (vector: DOMVector | null) => void;
  scrollVector: DOMVector | null;
  setScrollVector: (vector: DOMVector | null) => void;
}

const DragSelectionContext = createContext<DragSelectionValue | null>(null);

export function DragSelectionProvider({ children }: { children: ReactNode }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragVector, setDragVector] = useState<DOMVector | null>(null);
  const [scrollVector, setScrollVector] = useState<DOMVector | null>(null);

  return (
    <DragSelectionContext.Provider
      value={{
        isDragging,
        setIsDragging,
        dragVector,
        setDragVector,
        scrollVector,
        setScrollVector,
      }}
    >
      {children}
    </DragSelectionContext.Provider>
  );
}

export function useSelectionState() {
  const context = useContext(SelectionStateContext);
  if (!context) throw new Error('useSelectionState must be used within SelectionStateProvider');
  return context;
}

export function Root({ children }: { children?: ReactNode }) {
  const dragContext = useContext(DragSelectionContext);
  if (!dragContext) throw new Error('Root must be used within DragSelectionProvider');
  const { selectedItems, setSelectedItems, clearSelection } = useSelectionState();

  const isMobile = useIsMobile();

  const { isDragging, setIsDragging, dragVector, setDragVector, scrollVector, setScrollVector } = dragContext;

  const containerRef = useRef<HTMLDivElement>(null);

  const updateSelectedItems = useCallback(
    function updateSelectedItems(dragVector: DOMVector, scrollVector: DOMVector) {
      if (containerRef.current == null) return;
      const next: Record<string, boolean> = {};
      const containerRect = containerRef.current.getBoundingClientRect();
      containerRef.current.querySelectorAll('[data-item]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (containerRef.current == null) return;

        const itemRect = el.getBoundingClientRect();
        const translatedItemRect = new DOMRect(
          itemRect.x - containerRect.x + containerRef.current.scrollLeft,
          itemRect.y - containerRect.y + containerRef.current.scrollTop,
          itemRect.width,
          itemRect.height
        );

        if (!intersect(dragVector.add(scrollVector).toDOMRect(), translatedItemRect)) return;

        if (el.dataset.item && typeof el.dataset.item === 'string') {
          next[el.dataset.item] = true;
        }
      });
      if (!shallowEqual(next, selectedItems)) {
        setSelectedItems(next);
      }
    },
    [selectedItems]
  );

  useEffect(() => {
    if (!isDragging || containerRef.current == null) return;

    let handle = requestAnimationFrame(scrollTheLad);

    return () => cancelAnimationFrame(handle);

    function clamp(num: number, min: number, max: number) {
      return Math.min(Math.max(num, min), max);
    }

    function scrollTheLad() {
      if (containerRef.current == null || dragVector == null) return;

      const currentPointer = dragVector.toTerminalPoint();
      const containerRect = containerRef.current.getBoundingClientRect();

      const shouldScrollRight = containerRect.width - currentPointer.x < 20;
      const shouldScrollLeft = currentPointer.x < 20;
      const shouldScrollDown = containerRect.height - currentPointer.y < 20;
      const shouldScrollUp = currentPointer.y < 20;

      const left = shouldScrollRight
        ? clamp(20 - containerRect.width + currentPointer.x, 0, 20)
        : shouldScrollLeft
          ? -1 * clamp(20 - currentPointer.x, 0, 20)
          : undefined;

      const top = shouldScrollDown
        ? clamp(20 - containerRect.height + currentPointer.y, 0, 20)
        : shouldScrollUp
          ? -1 * clamp(20 - currentPointer.y, 0, 20)
          : undefined;

      if (top === undefined && left === undefined) {
        handle = requestAnimationFrame(scrollTheLad);
        return;
      }

      containerRef.current.scrollBy({
        left,
        top,
      });

      handle = requestAnimationFrame(scrollTheLad);
    }
  }, [isDragging, dragVector, updateSelectedItems]);

  useEffect(() => {
    if (containerRef.current == null) return;
    const selectedIds = Object.keys(selectedItems);
    const event = new CustomEvent('selectionchange', {
      detail: { selectedIds },
    });
    containerRef.current.dispatchEvent(event);
  }, [selectedItems]);

  const selectionRect =
    dragVector && scrollVector && containerRef.current
      ? dragVector
          .add(scrollVector)
          .clamp(new DOMRect(0, 0, containerRef.current.scrollWidth, containerRef.current.scrollHeight))
          .toDOMRect()
      : null;

  const onPointerDown = function (e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest('[data-no-drag-select="true"]')) {
      return;
    }

    if (isMobile) return;
    if (e.button !== 0) return;

    if ((e.target as HTMLElement).closest('[draggable="true"]')) {
      return;
    }

    if ((e.target as HTMLElement).closest('[data-folder="true"]')) {
      return;
    }

    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLAnchorElement ||
      (e.target as HTMLElement).closest('a') ||
      (e.target as HTMLElement).closest('input') ||
      (e.target as HTMLElement).closest('button')
    ) {
      return;
    }

    const containerRect = e.currentTarget.getBoundingClientRect();
    setDragVector(new DOMVector(e.clientX - containerRect.x, e.clientY - containerRect.y, 0, 0));
    setScrollVector(new DOMVector(e.currentTarget.scrollLeft, e.currentTarget.scrollTop, 0, 0));

    if (!(e.target as HTMLElement).closest('[data-item]')) {
      clearSelection();
    }

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  return (
    <div className="relative h-full">
      <div
        className="relative h-full focus:outline-hidden"
        ref={containerRef}
        onScroll={function (e) {
          if (dragVector == null || scrollVector == null) return;

          const { scrollLeft, scrollTop } = e.currentTarget;

          const nextScrollVector = new DOMVector(
            scrollVector.x,
            scrollVector.y,
            scrollLeft - scrollVector.x,
            scrollTop - scrollVector.y
          );

          setScrollVector(nextScrollVector);
          updateSelectedItems(dragVector, nextScrollVector);
        }}
        onPointerDown={onPointerDown}
        onPointerMove={function (e) {
          if (isMobile) return;
          if (dragVector == null || scrollVector == null) return;

          const containerRect = e.currentTarget.getBoundingClientRect();

          const nextDragVector = new DOMVector(
            dragVector.x,
            dragVector.y,
            e.clientX - containerRect.x - dragVector.x,
            e.clientY - containerRect.y - dragVector.y
          );
          const selection = document.getSelection();
          const elementFromPoint = document.elementFromPoint(e.clientX, e.clientY);

          if (!isDragging && nextDragVector.getDiagonalLength() < 10) return;
          if (!selection?.isCollapsed && selection?.focusNode?.textContent === elementFromPoint?.textContent) {
            setDragVector(null);
            return;
          }

          setIsDragging(true);

          selection?.removeAllRanges();

          setDragVector(nextDragVector);
          updateSelectedItems(nextDragVector, scrollVector);
        }}
        onPointerUp={function (e) {
          if (isMobile) return;
          if (dragVector == null) return;
          e.currentTarget.releasePointerCapture(e.pointerId);
          setIsDragging(false);
          setDragVector(null);
          setScrollVector(null);
        }}
        onLostPointerCapture={function () {
          if (isMobile) return;
          setIsDragging(false);
          setDragVector(null);
          setScrollVector(null);
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {selectionRect ? (
          <div
            style={{
              position: 'absolute',
              left: selectionRect.x,
              top: selectionRect.y,
              width: selectionRect.width,
              height: selectionRect.height,
              backgroundColor: 'rgba(0, 123, 255, 0.2)',
              border: '1px solid rgba(0, 123, 255, 0.5)',
              zIndex: 20,
              pointerEvents: 'none',
            }}
          />
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function Item({ children, id }: { id: string; children: ReactNode }) {
  const { selectedItems } = useSelectionState();
  return (
    <div data-item={id} className={clsx(selectedItems[id] ? 'border-blue-500' : '')}>
      {children}
    </div>
  );
}
